import { useState } from 'react';

import type { BeneficialOwnerType, CreateBeneficialOwnerDto } from '@/types/adminOnboarding';

interface BeneficialOwnerFormProps {
  defaultType?: BeneficialOwnerType;
  parentId?: string;
  onSubmit: (data: CreateBeneficialOwnerDto) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const initialState = (type: BeneficialOwnerType, parentId?: string): CreateBeneficialOwnerDto => ({
  type,
  name: '',
  ownershipPercentage: 0,
  rfc: '',
  email: '',
  phone: '',
  countryCode: '',
  nationality: '',
  address: '',
  parentId,
});

const inputClass =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

export const BeneficialOwnerForm: React.FC<BeneficialOwnerFormProps> = ({
  defaultType = 'individual',
  parentId,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [form, setForm] = useState<CreateBeneficialOwnerDto>(initialState(defaultType, parentId));

  const set = (field: keyof CreateBeneficialOwnerDto, value: string | number) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className={labelClass}>
            Nombre {form.type === 'company' ? 'de la empresa' : 'completo'} *
          </label>
          <input
            className={inputClass}
            value={form.name}
            onChange={e => set('name', e.target.value)}
            required
          />
        </div>

        <div>
          <label htmlFor="ownershipPercentage" className={labelClass}>
            Participación (%) *
          </label>
          <input
            className={inputClass}
            type="number"
            min="0.01"
            max="100"
            step="0.01"
            value={form.ownershipPercentage}
            onChange={e => set('ownershipPercentage', parseFloat(e.target.value) || 0)}
            required
          />
        </div>

        <div>
          <label htmlFor="rfc" className={labelClass}>
            RFC *
          </label>
          <input
            className={inputClass}
            value={form.rfc}
            onChange={e => set('rfc', e.target.value.toUpperCase())}
            maxLength={13}
            required
          />
        </div>

        {form.type === 'individual' && (
          <>
            <div>
              <label htmlFor="email" className={labelClass}>
                Email
              </label>
              <input
                className={inputClass}
                type="email"
                value={form.email ?? ''}
                onChange={e => set('email', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="phone" className={labelClass}>
                Teléfono
              </label>
              <input
                className={inputClass}
                value={form.phone ?? ''}
                onChange={e => set('phone', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="nationality" className={labelClass}>
                Nacionalidad
              </label>
              <input
                className={inputClass}
                value={form.nationality ?? ''}
                onChange={e => set('nationality', e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="address" className={labelClass}>
                Dirección
              </label>
              <input
                className={inputClass}
                value={form.address ?? ''}
                onChange={e => set('address', e.target.value)}
              />
            </div>
          </>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          disabled={isLoading}
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
          disabled={isLoading}
        >
          {isLoading ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </form>
  );
};
